import {useMutation} from "@apollo/client";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {
  GQL_PURCHASE_ADDONS,
  GQL_PURCHASE_REQUEST,
  PurchaseAddonsResponse,
  PurchaseRequestResponse,
} from "@/lib/queries/billing.ts";
import {GQL_ACCOUNT_USAGE} from "@/lib/queries/checks.ts";
import {ServiceUsage} from "@/lib/types/checks.ts";
import {CTAManageSubscription} from "@/pages/checks/components/form/InstantCheckout/CTAManageSubscription.tsx";
import {CTASendRequest} from "@/pages/checks/components/form/InstantCheckout/CTASendRequest.tsx";
import {CTAWithinPlan} from "@/pages/checks/components/form/InstantCheckout/CTAWithinPlan.tsx";
import {InstantCheckoutContainer} from "@/pages/checks/components/form/InstantCheckout/InstantCheckoutContainer.tsx";
import {ServiceNotAvailable} from "@/pages/checks/components/form/InstantCheckout/ServiceNotAvailable.tsx";
import {ServiceRequested} from "@/pages/checks/components/form/InstantCheckout/ServiceRequested.tsx";
import {InstantCheckoutSimpleWarning} from "@/pages/checks/components/form/InstantCheckout/SimpleWarning.tsx";
import {YouReachedMaximum} from "@/pages/checks/components/form/InstantCheckout/YouReachedMaximum.tsx";
import {YouReachedPlanMaximum} from "@/pages/checks/components/form/InstantCheckout/YouReachedPlanMaximum.tsx";

export interface InstantCheckoutAvailable {
  serviceUsage: ServiceUsage;
  planServiceLimit: number;
  plan: string;
  accountCanPurchase: boolean;
  userCanPurchase: boolean;
  onRequest: () => void;
  onRequestFailure: () => void;
}

export const InstantCheckoutAvailable = ({
  serviceUsage,
  accountCanPurchase,
  userCanPurchase,
  plan,
  planServiceLimit,
  ...handlers
}: InstantCheckoutAvailable) => {
  const [purchaseAddOns, {loading: purchaseLoading}] = useMutation<PurchaseAddonsResponse>(GQL_PURCHASE_ADDONS, {
    refetchQueries: [GQL_ACCOUNT_USAGE],
  });
  const [purchaseRequest, {loading: requestLoading}] = useMutation<PurchaseRequestResponse>(GQL_PURCHASE_REQUEST, {
    refetchQueries: [GQL_ACCOUNT_USAGE],
  });
  const {user} = useCurrentUserContext();
  if (!serviceUsage || !serviceUsage?.exhausted) {
    return null;
  }

  const hardLimit = serviceUsage.used >= planServiceLimit;
  const handleCTAClick = async () => {
    const variables = {
      addedValues: {
        limit_field: serviceUsage.limit_field,
        amount: serviceUsage.bundle_size,
      },
    };
    let response;
    if (userCanPurchase && !hardLimit) {
      const purchaseResponse = await purchaseAddOns({variables});
      response = purchaseResponse?.data?.purchaseAddOns;
    } else {
      const purchaseRequestResponse = await purchaseRequest({variables});
      response = purchaseRequestResponse?.data?.purchaseRequest;
    }
    if (response?.success) {
      handlers.onRequest();
    } else {
      handlers.onRequestFailure();
    }
  };

  // Covers the case for primary users of non-regular accounts. Everybody else
  // either can purchase, or can send purchase requests
  if (!accountCanPurchase && userCanPurchase) {
    return <InstantCheckoutSimpleWarning limit={serviceUsage.limit} limit_name={serviceUsage.limit_name} />;
  }

  if (!serviceUsage.limit) {
    return <InstantCheckoutContainer message={<ServiceNotAvailable />} />;
  }

  if (!userCanPurchase && serviceUsage.request_sent_on) {
    return (
      <InstantCheckoutContainer
        message={
          <ServiceRequested
            requestSent={serviceUsage.request_sent_on}
            timeFormat={user!.current_time_format}
            dateFormat={user!.current_date_format}
            timezone={user!.account!.timezone}
            limitName={serviceUsage.limit_name}
          />
        }
      />
    );
  }

  let message = <YouReachedMaximum userCanPurchase={userCanPurchase} serviceUsage={serviceUsage} />;
  let cta = (
    <CTAWithinPlan
      userCanPurchase={userCanPurchase}
      handleClick={handleCTAClick}
      disabled={purchaseLoading || requestLoading}
    />
  );
  if (hardLimit) {
    message = (
      <YouReachedPlanMaximum canPurchase={!!user?.is_primary} plan={plan} limitName={serviceUsage.limit_name} />
    );
    if (user?.is_primary) {
      cta = <CTAManageSubscription />;
    } else {
      cta = <CTASendRequest handleClick={handleCTAClick} disabled={purchaseLoading || requestLoading} />;
    }
  }

  return <InstantCheckoutContainer message={message} cta={cta} />;
};
